import React, { useEffect, useState } from 'react';
import Modal from '../../components/common/ui/Modal';
import { apiGetSubmissionLogs } from './api';

function fmtDT(iso?: string | null) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${m}-${day} ${hh}:${mm}`;
    } catch {
        return String(iso);
    }
}

type SubmissionLog = {
    id: string;
    action: 'SUBMITTED' | 'REJECTED' | 'APPROVED';
    earnedValue: number | null;
    comment: string | null;
    actorId: string | null;
    actedAt: string;
};

export default function WbsSubmissionLogsModal({
    open,
    onClose,
    projectId,
    submissionId,
    fridayISO,
}: {
    open: boolean;
    onClose: () => void;
    projectId: string;
    submissionId: string | null;
    fridayISO?: string | null;
}) {
    const [logs, setLogs] = useState<SubmissionLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !submissionId) return;

        setLoading(true);
        setErr(null);

        (async () => {
            try {
                const list = await apiGetSubmissionLogs(projectId, submissionId);
                setLogs(list ?? []);
            } catch (e: any) {
                setErr(e?.message ?? '로그를 불러오지 못했습니다.');
                setLogs([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [open, projectId, submissionId]);

    return (
        <Modal size="xl" open={open} title={`제출 이벤트 로그${fridayISO ? ` (${fridayISO})` : ''}`} onClose={onClose}>
            {!submissionId ? (
                <div className="text-sm text-gray-500">선택된 제출이 없습니다.</div>
            ) : loading ? (
                <div className="text-sm text-gray-500">불러오는 중…</div>
            ) : err ? (
                <div className="text-sm text-rose-700">{err}</div>
            ) : logs.length === 0 ? (
                <div className="text-sm text-gray-500">로그가 없습니다.</div>
            ) : (
                <div className="space-y-2">
                    {logs.map((l) => (
                        <div key={l.id} className="rounded-lg border px-3 py-2 text-xs">
                            <div className="flex items-center gap-2">
                                <div className="text-gray-400 w-36">{fmtDT(l.actedAt)}</div>
                                <div className="font-semibold w-24">{l.action}</div>
                                <div className="ml-auto tabular-nums">
                                    {l.earnedValue == null ? '—' : `${l.earnedValue}%`}
                                </div>
                            </div>
                            {l.comment ? (
                                <div className="mt-1 text-gray-600">{l.comment}</div>
                            ) : (
                                <div className="mt-1 text-gray-400">—</div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-4 text-right">
                <button className="text-xs rounded border px-2.5 py-1 hover:bg-gray-50" onClick={onClose}>
                    닫기
                </button>
            </div>
        </Modal>
    );
}
